import React, { useState } from "react";
import PropTypes from "prop-types";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "../../TableSortLabel";
import { GENERIC_LOADING_STRING } from "../../../../../constants";

/** @typedef {any} DataType */

/**
 * @typedef {Object} HeaderOption
 * @property {string} name
 * @property {function} [asc]
 * @property {function} [desc]
 * @property {string} [alignment]
 * @property {function} [onMouseEnter]
 * @property {function} [onMouseLeave]
 * */

/**
 * Generic frontend sortable Table Component
 * @param {Object} props
 * @param {string} tableClassName
 * @param {DataType[]} props.data
 * @param {function(DataType, boolean[false]): JSX.Element} props.dataMapper
 * @param {HeaderOption[]} props.headerOptions
 * @param {Object} [props.tableStyle] sx attribute passed to MUI Table component
 * @param {function(DataType): boolean} [props.isSelected]
 * @param {boolean} [props.loading]
 * @param {string} [props.loadingMessage]
 * @param {boolean} [props.stickyHeader]
 * @returns {Element}
 * @constructor
 */
export default function SortableTable({
                                        tableClassName,
                                        data,
                                        dataMapper,
                                        headerOptions,
                                        tableStyle,
                                        isSelected = () => false,
                                        initialSort = { col: null, dir: null },
                                        loading = false,
                                        loadingMessage = GENERIC_LOADING_STRING,
                                        stickyHeader = false,
                                      }) {
  const [tableSort, setTableSort] = useState(initialSort);

  if (tableSort.col && tableSort.dir) {
    data.sort(headerOptions.find(({ name }) => name === tableSort.col)[tableSort.dir]);
  }

  let tableContent;

  if (loading) {
    tableContent = <TableRow><TableCell colSpan={headerOptions.length}>{loadingMessage}</TableCell></TableRow>;
  } else if (data.length === 0) {
    tableContent = <TableRow>
      <TableCell colSpan={headerOptions.length}/>
    </TableRow>;
  } else {
    tableContent = data.map(dataRow => (dataMapper(dataRow, isSelected(dataRow))));
  }

  return (<Table sx={tableStyle} className={tableClassName} stickyHeader={stickyHeader}>
    <TableHead>
      <TableRow>
        {headerOptions.map(({ name, asc, desc, alignment, onMouseEnter, onMouseLeave }) => {
          let onClick;
          if (asc && desc) {
            onClick = () => {
              setTableSort(prevState => {
                if (prevState.col === name) {
                  return {
                    col: name, dir: prevState.dir === "asc" ? "desc" : "asc",
                  };
                } else {
                  return {
                    col: name, dir: "asc",
                  };
                }
              });
            };

            return (<TableCell
              key={name} className="sortable" onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave} align={alignment ? alignment : "center"}
              sx={{ fontWeight: "600", backgroundColor: "#2D5986", color: "white", fontSize: "14px" }}
            >
              <TableSortLabel onClick={onClick} active={tableSort.col === name}
                              direction={tableSort.col === name ? tableSort.dir : "asc"}>
                {name}
              </TableSortLabel>
            </TableCell>);
          } else {
            return (<TableCell
              key={name} onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave} align={alignment ? alignment : "center"}
              sx={{ fontWeight: "600", backgroundColor: "#2D5986", color: "white", fontSize: "14px" }}
            >
              {name}
            </TableCell>);
          }
        })}
      </TableRow>
    </TableHead>
    <TableBody>
      {tableContent}
    </TableBody>
  </Table>);
}

SortableTable.propTypes = {
  data: PropTypes.array.isRequired,
  dataMapper: PropTypes.func.isRequired,
  headerOptions: PropTypes.arrayOf(Object).isRequired,
  tableStyle: PropTypes.object,
  isSelected: PropTypes.func,
};
